import type { CaseReducer, PayloadAction } from '@reduxjs/toolkit';

import { isUnsafeObjectKey } from '~/common/utils/common.utils';

import type {
  SafetyEventHistoryState,
  SetSafetyEventHistorySessionPayload,
} from './safetyEventHistorySlice.model';

/**
 * Merges a partial view session into the bucket for `sessionId`.
 *
 * Shallow merge, so a caller can update one field (a page index, say) without
 * clobbering the filters or the continuation tokens alongside it.
 *
 * Session ids reaching this reducer are keys on a plain object, and they trace
 * back to route and search params, so an unsafe key is rejected before the
 * write the way `geofenceSlice` does for its own session state.
 */
const reduceSetSafetyEventHistorySession: CaseReducer<
  SafetyEventHistoryState,
  PayloadAction<SetSafetyEventHistorySessionPayload>
> = (state, action) => {
  const { sessionId, config } = action.payload;
  if (isUnsafeObjectKey(sessionId)) return;

  state.sessionConfigs[sessionId] = {
    ...state.sessionConfigs[sessionId],
    ...config,
  };
};

/** Drops the view session for `sessionId`, leaving other sessions untouched. */
const reduceResetSafetyEventHistorySession: CaseReducer<
  SafetyEventHistoryState,
  PayloadAction<string>
> = (state, action) => {
  if (isUnsafeObjectKey(action.payload)) return;

  delete state.sessionConfigs[action.payload];
};

/** Case reducers mounted by `safetyEventHistorySlice`. */
export const SAFETY_EVENT_HISTORY_REDUCERS = {
  reduceSetSafetyEventHistorySession,
  reduceResetSafetyEventHistorySession,
};
